import {
    createElement,
    isValidElement,
    type ComponentType,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import InteractiveCard from './InteractiveCard';
import { Heading, Text } from './Typography';

type ResourceIcon = ComponentType<{ className?: string }> | ReactNode;

interface AssignableResourceCardAction {
    label: ReactNode;
    onClick: () => void;
    icon?: ResourceIcon;
    accessory?: ReactNode;
    variant?: ButtonProps['variant'];
    disabled?: boolean;
    ariaDisabled?: boolean;
    /** When set, the button is wrapped in a tooltip (use with ariaDisabled so hover still fires). */
    tooltip?: string;
    className?: string;
}

interface AssignableResourceCardProps {
    title: ReactNode;
    description?: ReactNode;
    icon?: ResourceIcon;
    badges?: ReactNode;
    meta?: ReactNode;
    headerAction?: ReactNode;
    primaryAction?: AssignableResourceCardAction;
    secondaryAction?: AssignableResourceCardAction;
    onOpen?: () => void;
    className?: string;
    contentClassName?: string;
}

function renderIcon(icon?: ResourceIcon, className = 'h-5 w-5') {
    if (!icon) return null;
    if (isValidElement(icon)) return icon;

    return createElement(icon as ComponentType<{ className?: string }>, {
        className,
    });
}

function ResourceActionButton({
    action,
    emphasis,
}: {
    action: AssignableResourceCardAction;
    emphasis: 'primary' | 'secondary';
}) {
    const IconNode = renderIcon(action.icon, 'h-4 w-4');

    const button = (
        <Button
            type="button"
            variant={action.variant ?? (emphasis === 'primary' ? 'tint' : 'surface')}
            disabled={action.disabled}
            aria-disabled={action.ariaDisabled}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action.onClick();
            }}
            className={cn(
                'w-full justify-center',
                emphasis === 'primary' ? 'sm:flex-1' : 'sm:w-auto',
                action.className,
            )}
        >
            {IconNode}
            {action.label}
            {action.accessory}
        </Button>
    );

    if (!action.tooltip) return button;

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent>{action.tooltip}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export default function AssignableResourceCard({
    title,
    description,
    icon,
    badges,
    meta,
    headerAction,
    primaryAction,
    secondaryAction,
    onOpen,
    className,
    contentClassName,
}: AssignableResourceCardProps) {
    const IconNode = renderIcon(icon);
    const hasActions = Boolean(primaryAction || secondaryAction);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onOpen || event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onOpen();
    };

    return (
        <InteractiveCard
            interactive={Boolean(onOpen)}
            hoverable
            role={onOpen ? 'button' : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={handleKeyDown}
            className={className}
        >
            <CardContent className={cn('flex h-full flex-col gap-4 p-4 sm:p-6', contentClassName)}>
                <div className="flex min-w-0 items-start gap-4">
                    {IconNode ? (
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-brand/15 bg-brand-subtle text-brand shadow-sm">
                            {IconNode}
                        </div>
                    ) : null}

                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-start justify-between gap-3">
                            <div className="min-w-0 space-y-2">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <Heading variant="card">{title}</Heading>
                                    {badges}
                                </div>
                                {meta}
                                {description ? (
                                    <Text variant="description" className="line-clamp-3">
                                        {description}
                                    </Text>
                                ) : null}
                            </div>
                            {headerAction ? (
                                <div
                                    className="shrink-0"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {headerAction}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                {hasActions ? (
                    <div className="mt-auto flex flex-col gap-2 sm:flex-row">
                        {primaryAction ? (
                            <ResourceActionButton action={primaryAction} emphasis="primary" />
                        ) : null}
                        {secondaryAction ? (
                            <ResourceActionButton action={secondaryAction} emphasis="secondary" />
                        ) : null}
                    </div>
                ) : null}
            </CardContent>
        </InteractiveCard>
    );
}
